// @givmo/board — EVERY word CBG-RC12-READ's screens add, in one place: who may open a closed
// session's minutes (D-CBG-25). They are SENT TO the people who were in the session; every current
// director may OPEN them, each opening recorded in their history, in person or through an AI for
// its person (backend `board_minutes.md` §O1–§O12).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the frames for building on 2026-10-12 22:52 PDT ("looks good"),
// with every word a draft, and then the words themselves in copy round CBG-RC12-COPY1's second
// round on 2026-10-13 14:48 PDT ("4)ok"), every line as built on its page
// (`scratchpad/2026-10-13-rc12read-copy-round/copy-review.html`). Each line names the frame that
// draws it (`d1-entry`, `d2-open`, `d3-opened`, `c3-history`; the design branch
// `design/board-minutes-approval-closed-sessions` @ `8d5c8ee8`), or "not drawn" for a state no frame
// shows. The four RC12-REC lines RC12-READ completed ("…, and any director can open it/them") are
// the frames' own words and live where they always did, `../in-meeting/closed-session-words.ts`.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what the board recorded, and where it goes, never a
// conclusion about a matter, a conflict or a decision. No pronoun for a named person.

/** The closed-minutes words, approved in CBG-RC12-COPY1's second round. */
export const closedMinutesWords = {
  // ── The director's meeting page ("How the meeting ran", frame d1-entry) ─────────────────────────

  // Drawn, d1-entry: the way to a closed session's own minutes. Also the button that, with the
  // signing window shut, brings up the confirmation first (agreed with the orchestrator 2026-10-13
  // 11:54 PDT: no new words for that state).
  openItsMinutes: 'Open its minutes',
  // Drawn, d1-entry: the director's own seat stepped out of the session.
  youSteppedOut: 'You stepped out of this session for a conflict of interest.',
  // Drawn, d1-entry: "7:20 PM to 7:50 PM". Not drawn: only the start.
  sessionTimes: (entered: string, exited: string | null) =>
    exited === null ? `From ${entered}` : `${entered} to ${exited}`,
  // Not drawn: a closed session recorded with no general purpose.
  noPurpose: 'No general purpose recorded',
  // Drawn, d1-entry: the decisions a closed session reports.
  reportedHeading: 'Reported from the closed session',
  // Drawn, d1-entry: "Recorded vote: 4 for, 0 against, 0 abstaining".
  recordedVote: (votesFor: number, against: number, abstaining: number) =>
    `Recorded vote: ${votesFor} for, ${against} against, ${abstaining} abstaining`,
  // Drawn, d1-entry.
  recordedAsRecused: (names: string) => `Recorded as recused: ${names}`,

  // ── The step before opening (frame d2-open) ─────────────────────────────────────────────────────

  // Drawn, d2-open.
  stepTitle: 'Minutes of a closed session',
  // Drawn, d2-open: the director's seat stepped out.
  youWerentInItSteppedOut:
    'You weren’t in this session: you stepped out for a conflict of interest.',
  // Not drawn: a director who wasn't in it and didn't step out.
  youWerentInIt: 'You weren’t in this session.',
  // Drawn, d2-open.
  asADirector:
    'As a director, you can open its minutes. Opening them is recorded in their history, with your name.',
  // Drawn, d2-open.
  openTheMinutes: 'Open the minutes',
  // Drawn, d2-open.
  backToTheMeeting: 'Back to the meeting',

  // ── Opened (frame d3-opened) ────────────────────────────────────────────────────────────────────

  // Drawn, d3-opened: "… · Feb 16, 2027 at 10:04 AM".
  openedByYou: (when: string) => `Opened by you, a director who wasn’t in this session · ${when}`,
  // Drawn, d3-opened.
  inTheirHistory: 'This is in the minutes’ history, which anyone who can open them reads.',

  // ── The record's history (frame c3-history) ─────────────────────────────────────────────────────

  // Drawn, c3-history: an `opened` event's title.
  opened: 'Opened',
  // Drawn, c3-history: an opening in person.
  openedBy: (name: string) => `By ${name}, a director who wasn’t in this session`,
  // Drawn, c3-history: an opening through an AI for its person; the AI that acted is "an AI
  // assistant", never its tool's name (D-CBG-45 as amended, 2026-10-29 08:57 PDT).
  openedThrough: (name: string) =>
    `Through an AI assistant for ${name}, a director who wasn’t in this session`,
} as const;
